import React from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { AuthorFragmentFragment } from 'gql/graphql-operations';
import twMerge from 'utilities/src/twMerge';
import Tag from './_Tag';
import VerifiedTopTalentBadge from './VerifiedTopTalentBadge';
import socialMediaConfig from './_utils/socialMedia';
import { getAllTags, WpTag } from './_utils/tags';

const FALLBACK_AUTHOR_IMAGE =
  'https://storage.googleapis.com/website-wordpress/uploads/user_profiles/BairesDev-Editorial-Team.png';

type PostAuthorProps = {
  author: AuthorFragmentFragment;
  classes?: string;
  taxonomies?: number[];
  /** "Verified Top Talent" badge on the top-right edge */
  showBadge?: boolean;
  /** Keep bio and expertise stacked at every width (the large card puts them side by side) */
  stackDetails?: boolean;
};

function PostAuthor({ author, classes, taxonomies, showBadge = true, stackDetails = false }: PostAuthorProps) {
  const allTags: WpTag[] = getAllTags(author);

  // Reorder allTags to place matching taxonomies first
  const reorderedTags = React.useMemo(() => {
    if (!taxonomies || taxonomies.length === 0) {
      return allTags;
    }

    const taxonomySet = new Set(taxonomies);
    const matchingTags: WpTag[] = [];
    const nonMatchingTags: WpTag[] = [];

    allTags.forEach((tag) => {
      if (taxonomySet.has(tag.databaseId)) {
        matchingTags.push(tag);
      } else {
        nonMatchingTags.push(tag);
      }
    });

    return [...matchingTags, ...nonMatchingTags];
  }, [allTags, taxonomies]);

  const authorHref = `/author/${author.slug}/`;
  const yearsOfExperience = author.customFields?.yearsOfExperience;

  return (
    <div className={twMerge('bg-site-neutrals-0 shadow-lv2 relative rounded-xl', classes)}>
      {showBadge && <VerifiedTopTalentBadge className="lgS:right-[1.875rem] -top-[0.5625rem] right-4" />}

      <div className="lgS:items-center lgS:pt-4 flex items-start gap-4 px-4 pt-6">
        <Image
          alt=""
          role="presentation"
          src={author.customFields?.authorImage ?? FALLBACK_AUTHOR_IMAGE}
          width={114}
          height={114}
          className="size-[7.125rem] shrink-0 rounded-lg object-cover"
          unoptimized
        />

        <div className="flex min-w-0 flex-1 flex-col gap-4">
          <div className="flex flex-col">
            <p className="site-display-xs text-site-neutrals-900 font-normal">
              By{' '}
              <Link
                href={authorHref}
                prefetch={false}
                className="hover:text-site-primary-600 font-bold underline transition-colors"
              >
                {author.name}
              </Link>
            </p>

            <div className="lgS:flex-row lgS:items-center lgS:gap-2 flex flex-col">
              {author.customFields?.bdRole && (
                <span className="site-paragraph-m text-site-neutrals-800">{author.customFields.bdRole}</span>
              )}
              {yearsOfExperience && (
                <>
                  <span
                    aria-hidden
                    className="bg-site-neutrals-100 lgS:block hidden size-1.5 shrink-0 rounded-full"
                  />
                  <span className="site-paragraph-s text-site-neutrals-500">
                    {yearsOfExperience} Years of Experience
                  </span>
                </>
              )}
            </div>
          </div>

          <ul className="flex items-center gap-2">
            {socialMediaConfig.map(({ key, src, alt, defaultUrl }) => {
              const url = (author.customFields?.[key] as string | undefined) || defaultUrl;
              return (
                <li key={key}>
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`${author.name ?? 'Author'} on ${alt}`}
                    className="bg-site-neutrals-400 hover:bg-site-primary-600 block size-6 transition-colors"
                    style={{
                      mask: `url(${src}) center / 100% no-repeat`,
                      WebkitMask: `url(${src}) center / 100% no-repeat`,
                    }}
                  />
                </li>
              );
            })}
          </ul>
        </div>
      </div>

      <div className={twMerge('flex flex-col gap-5 px-5 pb-5 pt-4', !stackDetails && 'lgS:flex-row')}>
        {author.description && (
          <p className="site-paragraph-s text-site-neutrals-700 flex-1">{author.description}</p>
        )}
        {reorderedTags.length > 0 && (
          <div className="flex flex-1 flex-col gap-2">
            <p className="site-overline-s text-site-neutrals-400">Expertise</p>
            <ul className="flex flex-wrap gap-3">
              {reorderedTags.map((tag) => (
                <li key={`${tag.type}-${tag.name}`}>
                  <Tag label={tag.name} url={tag.url} className="text-site-neutrals-900 rounded-md px-2 py-0.5" />
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

export default PostAuthor;
